import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, EASE } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const HERO_IMG =
  "https://images.pexels.com/photos/5938553/pexels-photo-5938553.jpeg?auto=compress&cs=tinysrgb&w=1920";

export default function Hero({ ready }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.12]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen min-h-[660px] md:h-[96vh] overflow-hidden bg-[#F0E4D3]"
    >
      {/* campaign photograph */}
      <motion.div className="absolute inset-0 overflow-hidden" style={{ y: imgY, scale: imgScale }}>
        <motion.img
          data-testid="hero-product-image"
          src={HERO_IMG}
          alt="Applying Vivoré Luxe Hydrating Cream to smooth skin"
          className="w-full h-full object-cover object-[50%_30%] animate-[kenburns_24s_ease-in-out_infinite_alternate]"
          initial={{ scale: 1.1, opacity: 0 }}
          animate={ready ? { scale: 1.02, opacity: 1 } : {}}
          transition={{ duration: 2, ease: EASE }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(250deg, rgba(20,16,12,0.22) 0%, rgba(20,16,12,0.06) 30%, rgba(20,16,12,0) 50%)",
          }}
        />
      </motion.div>

      {/* headline — right side, per the mockup */}
      <motion.div
        className="absolute top-[30%] left-[6%] right-[6%] md:right-[7%] text-right z-10"
        style={{ opacity: contentOpacity }}
      >
        <h1 className="font-sans uppercase text-[#14100C] text-[7vw] sm:text-4xl md:text-[2.8rem] lg:text-[3.3rem] leading-[1.24] tracking-[0.13em] [text-shadow:0_0_22px_rgba(248,245,242,0.95),0_0_55px_rgba(248,245,242,0.85)]">
          <MaskedLines
            start={ready}
            delay={0.7}
            stagger={0.18}
            lines={[
              <span key="1">A new ritual</span>,
              <span key="2">for your skin.</span>,
            ]}
          />
        </h1>
      </motion.div>

      {/* thin vertical rule — from the headline down through the jar, per the mockup */}
      <motion.div
        aria-hidden
        className="absolute right-[36%] md:right-[36.5%] top-[42%] h-[58%] w-px bg-[#14100C]/80 z-10"
        initial={{ scaleY: 0 }}
        animate={ready ? { scaleY: 1 } : {}}
        transition={{ duration: 1.6, delay: 1.2, ease: EASE }}
        style={{ transformOrigin: "top" }}
      />

      {/* vi monogram — bottom left, per the mockup */}
      <motion.div
        className="absolute left-[4%] md:left-[4.5%] bottom-[4%] z-10"
        initial={{ opacity: 0, y: 30 }}
        animate={ready ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 1.4, delay: 1.5, ease: EASE }}
      >
        <img
          src="/images/logo-vi-black.png"
          alt="vi"
          className="h-12 md:h-20 w-auto drop-shadow-[0_2px_16px_rgba(0,0,0,0.2)]"
        />
      </motion.div>

      {/* scroll cue */}
      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("shop")}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 font-sans text-[10px] uppercase tracking-widest2 text-[#14100C]/50 hover:text-[#14100C] transition-colors"
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1 } : {}}
        transition={{ delay: 2.5, duration: 1 }}
        aria-label="Scroll to product"
      >
        Scroll
      </motion.button>
    </section>
  );
}
